import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Vibrate, VibrateOff, Sun, SunDim, Waves, Ban } from "lucide-react";
import {
  HAPTICS_SUPPORTED,
  HAPTIC_PATTERNS,
  vibrate,
  loadHapticsPreference,
  saveHapticsPreference,
  shouldShowHapticsHint,
  markHapticsHintSeen,
} from "@/lib/haptics";
import {
  loadBreathPulsePreference,
  saveBreathPulsePreference,
} from "@/lib/breathColorPulse";
import {
  loadBreathSyncPreference,
  saveBreathSyncPreference,
} from "@/lib/breathSync";
import { BreathColorPulse } from "@/components/calm/BreathColorPulse";
import { toast } from "sonner";

// 4-7-8 breathing (Dr. Andrew Weil's pattern):
//   Inhale 4s → Hold 7s → Exhale 8s. Repeat.
// The scale of the orb follows the phase: grows during inhale, holds full, shrinks during exhale.
const PHASES = [
  { key: "inhale", label: "breathe in", seconds: 4, scale: 1.6 },
  { key: "hold", label: "hold", seconds: 7, scale: 1.6 },
  { key: "exhale", label: "breathe out", seconds: 8, scale: 0.85 },
];

const CYCLE_OPTIONS = [3, 4];
const DEFAULT_TOTAL_CYCLES = 4;
const CYCLES_STORAGE_KEY = "stillwater.breathingCycles";

const loadInitialCycles = () => {
  try {
    const raw = localStorage.getItem(CYCLES_STORAGE_KEY);
    const n = raw ? parseInt(raw, 10) : NaN;
    return CYCLE_OPTIONS.includes(n) ? n : DEFAULT_TOTAL_CYCLES;
  } catch (_) {
    return DEFAULT_TOTAL_CYCLES;
  }
};

export const BreathingExercise = ({ open, onClose, onBreathPhase, onBreathEnd }) => {
  const [cycle, setCycle] = useState(0); // 0..totalCycles
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [remaining, setRemaining] = useState(PHASES[0].seconds);
  const [done, setDone] = useState(false);
  const [totalCycles, setTotalCycles] = useState(loadInitialCycles);
  const [hapticsOn, setHapticsOn] = useState(loadHapticsPreference);
  const [pulseOn, setPulseOn] = useState(loadBreathPulsePreference);
  const [breathSyncOn, setBreathSyncOn] = useState(loadBreathSyncPreference);
  const hapticsRef = useRef(hapticsOn);
  const breathSyncRef = useRef(breathSyncOn);
  useEffect(() => {
    hapticsRef.current = hapticsOn;
  }, [hapticsOn]);
  useEffect(() => {
    breathSyncRef.current = breathSyncOn;
    // If the user turns audio sync off mid-session, immediately return the
    // ambient bed to its neutral gain — don't leave it stuck at a peak.
    if (!breathSyncOn && typeof onBreathEnd === "function") onBreathEnd();
  }, [breathSyncOn, onBreathEnd]);

  // Reset whenever the overlay opens/closes.
  useEffect(() => {
    if (!open) return;
    setCycle(0);
    setPhaseIdx(0);
    setRemaining(PHASES[0].seconds);
    setDone(false);
    // Soft opening tap — signal the body that we're starting the first inhale.
    if (hapticsRef.current) vibrate(HAPTIC_PATTERNS.inhale);
    // Kick off the audio modulation on the first inhale so the ambient bed
    // swells with the very first breath.
    if (breathSyncRef.current && typeof onBreathPhase === "function") {
      onBreathPhase(PHASES[0].key, PHASES[0].seconds);
    }
    // First-time gentle hint that haptics is on (only on touch/small screens).
    if (shouldShowHapticsHint()) {
      toast("gentle taps are on", {
        description:
          "you'll feel a soft cue at each step. tap the haptics pill any time to turn them off.",
        duration: 6500,
      });
      markHapticsHintSeen();
    }
  }, [open, onBreathPhase]);

  // When the overlay closes or the session ends, gracefully return the
  // ambient bed to its neutral (pre-session) gain so a paused/closed exercise
  // never leaves the audio stuck at an inhale peak.
  useEffect(() => {
    if (!open || done) {
      if (typeof onBreathEnd === "function") onBreathEnd();
    }
  }, [open, done, onBreathEnd]);

  const handleCyclesChange = (n) => {
    setTotalCycles(n);
    try {
      localStorage.setItem(CYCLES_STORAGE_KEY, String(n));
    } catch (_) {
      /* silent */
    }
    // If the user has already completed enough cycles for the new lower value,
    // gracefully finish rather than yanking them backwards.
    if (cycle >= n) {
      setDone(true);
    }
  };

  const handleHapticsToggle = () => {
    setHapticsOn((prev) => {
      const next = !prev;
      saveHapticsPreference(next);
      // Preview tap when turning on, so the user feels what they enabled.
      if (next) vibrate(HAPTIC_PATTERNS.inhale);
      return next;
    });
  };

  const handlePulseToggle = () => {
    setPulseOn((prev) => {
      const next = !prev;
      saveBreathPulsePreference(next);
      return next;
    });
  };

  const handleBreathSyncToggle = () => {
    setBreathSyncOn((prev) => {
      const next = !prev;
      saveBreathSyncPreference(next);
      // If turning on mid-cycle, immediately engage the current phase so the
      // user hears the effect without waiting for the next transition.
      if (next && !done && typeof onBreathPhase === "function") {
        onBreathPhase(PHASES[phaseIdx].key, remaining || PHASES[phaseIdx].seconds);
      }
      return next;
    });
  };

  // Single tick loop. Runs while overlay is open and exercise isn't done.
  useEffect(() => {
    if (!open || done) return;
    const id = setInterval(() => {
      // Pure functional update — no side effects, no reads of other state.
      setRemaining((r) => (r > 1 ? r - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [open, done]);

  // When remaining hits 0, advance the phase / cycle. This runs OUTSIDE the
  // tick so React can commit state cleanly without double-invocation issues.
  useEffect(() => {
    if (!open || done) return;
    if (remaining !== 0) return;
    const nextIdx = (phaseIdx + 1) % PHASES.length;
    const startingNewCycle = nextIdx === 0;
    if (startingNewCycle) {
      const nextCycle = cycle + 1;
      if (nextCycle >= totalCycles) {
        if (hapticsOn) vibrate(HAPTIC_PATTERNS.finish);
        setDone(true);
        return;
      }
      setCycle(nextCycle);
    }
    if (hapticsOn) {
      // Cycle-boundary taps are slightly richer than mid-cycle taps.
      const pattern = startingNewCycle
        ? HAPTIC_PATTERNS.cycle
        : HAPTIC_PATTERNS[PHASES[nextIdx].key];
      vibrate(pattern);
    }
    // Ride the audio bed to match the incoming phase — swells on inhale/hold,
    // softens on exhale. Silently no-ops if the user hasn't started audio.
    if (breathSyncRef.current && typeof onBreathPhase === "function") {
      onBreathPhase(PHASES[nextIdx].key, PHASES[nextIdx].seconds);
    }
    setPhaseIdx(nextIdx);
    setRemaining(PHASES[nextIdx].seconds);
  }, [remaining, open, done, phaseIdx, cycle, totalCycles, hapticsOn, onBreathPhase]);

  if (!open) return null;

  const phase = PHASES[phaseIdx];

  return (
    <AnimatePresence>
      <motion.div
        key="breathing-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 1.2, ease: "easeInOut" }}
        className="fixed inset-0 z-30 overflow-y-auto overscroll-contain"
        data-testid="breathing-overlay"
      >
        <div className="fixed inset-0 bg-black/45 backdrop-blur-2xl pointer-events-none" aria-hidden="true" />

        {/* Peripheral colour-therapy pulse — subtle wash tied to the current
            breath phase. See BreathColorPulse.jsx for the palette + reasoning. */}
        <BreathColorPulse
          active={pulseOn && !done}
          phase={phase.key}
          seconds={phase.seconds}
        />

        <button
          onClick={onClose}
          className="fixed w-10 h-10 rounded-full flex items-center justify-center bg-white/5 border border-white/10 backdrop-blur-xl text-white/70 hover:bg-white/10 hover:text-white transition-colors duration-500 z-10"
          style={{
            top: "calc(env(safe-area-inset-top) + 1rem)",
            right: "calc(env(safe-area-inset-right) + 1rem)",
          }}
          data-testid="breathing-close-btn"
          aria-label="Close breathing exercise"
        >
          <X size={18} strokeWidth={1.5} />
        </button>

        <div className="relative min-h-full flex items-center justify-center px-6 py-24 sm:py-20">
        <div className="relative w-full max-w-xl flex flex-col items-center text-center">
          {done ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.4, ease: "easeInOut" }}
              className="flex flex-col items-center gap-6"
              data-testid="breathing-done"
            >
              <div
                className="w-64 h-64 rounded-full"
                style={{
                  background:
                    "radial-gradient(circle, rgba(167,139,250,0.5) 0%, rgba(34,211,238,0.15) 45%, transparent 75%)",
                  filter: "blur(30px)",
                }}
              />
              <h2 className="font-heading italic text-white/95 text-3xl sm:text-4xl">
                well done. you moved with your breath.
              </h2>
              <p className="text-white/60 text-sm sm:text-base max-w-md leading-relaxed">
                Rest here as long as you like. When you are ready, close this
                and return to the water.
              </p>
              <button
                onClick={onClose}
                className="mt-4 inline-flex items-center gap-2 rounded-full px-7 py-3 bg-white/10 hover:bg-white/20 border border-white/15 text-white/90 backdrop-blur-xl"
                data-testid="breathing-finish-btn"
              >
                <Sparkles size={16} strokeWidth={1.5} />
                <span>return</span>
              </button>
            </motion.div>
          ) : (
            <div className="flex flex-col items-center gap-6 sm:gap-8">
              {/* Breathing orb — shrinks a touch on mobile so it doesn't crowd
                  the countdown + cycle picker below. */}
              <div className="relative flex items-center justify-center h-64 w-64 sm:h-[22rem] sm:w-[22rem]">
                <motion.div
                  className="absolute rounded-full"
                  animate={{ scale: phase.scale }}
                  transition={{ duration: phase.seconds, ease: "easeInOut" }}
                  style={{
                    width: "12rem",
                    height: "12rem",
                    background:
                      "radial-gradient(circle, rgba(167,139,250,0.6) 0%, rgba(34,211,238,0.25) 55%, transparent 80%)",
                    filter: "blur(24px)",
                  }}
                  data-testid="breathing-orb"
                />
                <motion.div
                  className="relative w-32 h-32 sm:w-40 sm:h-40 rounded-full border border-white/25 backdrop-blur-md bg-white/5 flex items-center justify-center"
                  animate={{ scale: phase.scale }}
                  transition={{ duration: phase.seconds, ease: "easeInOut" }}
                >
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={`${phase.key}-${cycle}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.9 }}
                      className="font-heading italic text-white/95 text-xl"
                      data-testid="breathing-phase-label"
                    >
                      {phase.label}
                    </motion.span>
                  </AnimatePresence>
                </motion.div>
              </div>

              <div className="flex flex-col items-center gap-2">
                <span
                  className="font-heading text-white/95 text-5xl sm:text-6xl leading-none tabular-nums"
                  data-testid="breathing-countdown"
                >
                  {remaining || phase.seconds}
                </span>
                <span className="text-[11px] tracking-[0.25em] uppercase text-white/40">
                  seconds
                </span>
              </div>

              <div
                className="flex items-center gap-2"
                data-testid="breathing-progress"
              >
                {Array.from({ length: totalCycles }).map((_, i) => (
                  <span
                    key={i}
                    className={`h-1 rounded-full transition-all duration-700 ${
                      i === cycle
                        ? "w-8 bg-white/80"
                        : i < cycle
                          ? "w-4 bg-white/40"
                          : "w-4 bg-white/15"
                    }`}
                  />
                ))}
              </div>

              <p className="text-white/50 text-xs tracking-widest uppercase">
                cycle {Math.min(cycle + 1, totalCycles)} of {totalCycles} · 4-7-8
              </p>

              <div
                className="mt-2 flex items-center gap-1 rounded-full bg-white/5 border border-white/10 backdrop-blur-xl px-1.5 py-1"
                data-testid="breathing-cycles-picker"
                role="radiogroup"
                aria-label="Number of breathing cycles"
              >
                <span className="text-[10px] tracking-widest uppercase text-white/40 pl-2 pr-1">
                  cycles
                </span>
                {CYCLE_OPTIONS.map((n) => {
                  const active = totalCycles === n;
                  return (
                    <button
                      key={n}
                      role="radio"
                      aria-checked={active}
                      onClick={() => handleCyclesChange(n)}
                      className={`min-w-[2rem] px-2 py-1 rounded-full text-xs transition-colors duration-500 ${
                        active
                          ? "bg-white/20 text-white/95"
                          : "text-white/50 hover:text-white/80 hover:bg-white/5"
                      }`}
                      data-testid={`breathing-cycles-${n}`}
                      data-active={active}
                    >
                      {n}
                    </button>
                  );
                })}
              </div>

              <div className="flex flex-wrap items-center justify-center gap-2">
                {HAPTICS_SUPPORTED && (
                  <button
                    onClick={handleHapticsToggle}
                    aria-pressed={hapticsOn}
                    className={`inline-flex items-center gap-2 rounded-full backdrop-blur-xl border px-3 py-1.5 text-[11px] tracking-widest uppercase transition-colors duration-500 ${
                      hapticsOn
                        ? "bg-white/15 border-white/20 text-white/85"
                        : "bg-white/5 border-white/10 text-white/45 hover:text-white/70"
                    }`}
                    data-testid="breathing-haptics-toggle"
                    data-active={hapticsOn}
                    title={
                      hapticsOn
                        ? "Haptic taps are on"
                        : "Haptic taps are off"
                    }
                  >
                    {hapticsOn ? (
                      <Vibrate size={13} strokeWidth={1.5} />
                    ) : (
                      <VibrateOff size={13} strokeWidth={1.5} />
                    )}
                    <span>haptics · {hapticsOn ? "on" : "off"}</span>
                  </button>
                )}

                <button
                  onClick={handlePulseToggle}
                  aria-pressed={pulseOn}
                  className={`inline-flex items-center gap-2 rounded-full backdrop-blur-xl border px-3 py-1.5 text-[11px] tracking-widest uppercase transition-colors duration-500 ${
                    pulseOn
                      ? "bg-white/15 border-white/20 text-white/85"
                      : "bg-white/5 border-white/10 text-white/45 hover:text-white/70"
                  }`}
                  data-testid="breathing-pulse-toggle"
                  data-active={pulseOn}
                  title={
                    pulseOn
                      ? "Colour pulse is on"
                      : "Colour pulse is off"
                  }
                >
                  {pulseOn ? (
                    <Sun size={13} strokeWidth={1.5} />
                  ) : (
                    <SunDim size={13} strokeWidth={1.5} />
                  )}
                  <span>colour · {pulseOn ? "on" : "off"}</span>
                </button>

                <button
                  onClick={handleBreathSyncToggle}
                  aria-pressed={breathSyncOn}
                  className={`inline-flex items-center gap-2 rounded-full backdrop-blur-xl border px-3 py-1.5 text-[11px] tracking-widest uppercase transition-colors duration-500 ${
                    breathSyncOn
                      ? "bg-white/15 border-white/20 text-white/85"
                      : "bg-white/5 border-white/10 text-white/45 hover:text-white/70"
                  }`}
                  data-testid="breathing-sync-toggle"
                  data-active={breathSyncOn}
                  title={
                    breathSyncOn
                      ? "Ambient audio rises and falls with your breath"
                      : "Ambient audio stays steady"
                  }
                >
                  {breathSyncOn ? (
                    <Waves size={13} strokeWidth={1.5} />
                  ) : (
                    <Ban size={13} strokeWidth={1.5} />
                  )}
                  <span>sync · {breathSyncOn ? "on" : "off"}</span>
                </button>
              </div>
            </div>
          )}
        </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BreathingExercise;
